import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Task2 } from '@/types/tasks';
import { toast } from '@/hooks/use-toast';
import { getObraById } from '@/lib/obraCache';
import { getMembroById } from '@/services/profilesCache';
import type { Json, TablesUpdate } from '@/integrations/supabase/types';

interface CreateTaskData {
  title: string;
  description?: string;
  priority: 'low' | 'medium' | 'high';
  setor?: string;
  assigneeId?: string;
  startDate?: Date;
  endDate?: Date;
  workId?: string;
  projectId?: string;
  status?: string;
}

interface TaskFromDB {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  due_date: string | null;
  start_date: string | null;
  work_tags: string[];
  assignee_id: string | null;
  project_id: string | null;
  estimated_hours: number | null;
  actual_hours: number | null;
  progress: number;
  comments: unknown;
  checklist: unknown;
  followers: unknown;
  created_at: string;
  updated_at: string;
}

// As colunas jsonb chegam como `unknown`; normalizamos para array e revivemos
// as datas (que viajam como string) para o formato que os componentes esperam.
function parseComments(raw: unknown): Task2['comments'] {
  if (!Array.isArray(raw)) return [];
  return raw.map((c: Record<string, unknown>) => ({
    id: String(c.id ?? Date.now()),
    userId: String(c.userId ?? ''),
    userName: String(c.userName ?? 'Usuário'),
    userAvatar: c.userAvatar as string | undefined,
    message: String(c.message ?? ''),
    createdAt: c.createdAt ? new Date(c.createdAt as string) : new Date(),
    isPrivate: Boolean(c.isPrivate),
  }));
}

function parseChecklist(raw: unknown): Task2['checklist'] {
  if (!Array.isArray(raw)) return [];
  return raw.map((i: Record<string, unknown>) => ({
    id: String(i.id ?? Date.now()),
    title: String(i.title ?? ''),
    completed: Boolean(i.completed),
    assigneeId: i.assigneeId as string | undefined,
    assigneeName: i.assigneeName as string | undefined,
    dueDate: i.dueDate ? new Date(i.dueDate as string) : undefined,
  }));
}

// Colunas criadas pela migration 20260726190000. Enquanto ela não roda num
// ambiente, a gravação delas falha — o app degrada em vez de perder a edição.
const COLUNAS_NOVAS = ['start_date', 'comments', 'checklist', 'followers'] as const;
let colunasNovasDisponiveis = true;

/** PostgREST devolve PGRST204 quando a coluna não existe no schema. */
function isMissingColumnError(error: { code?: string; message?: string }): boolean {
  if (error.code === 'PGRST204' || error.code === '42703') return true;
  const msg = error.message ?? '';
  return COLUNAS_NOVAS.some((c) => msg.includes(`'${c}'`) || msg.includes(`"${c}"`));
}

function parseFollowers(raw: unknown): Task2['followers'] {
  if (!Array.isArray(raw)) return [];
  return raw.map((f: Record<string, unknown>) => ({
    id: String(f.id ?? ''),
    name: String(f.name ?? 'Usuário'),
    avatar: f.avatar as string | undefined,
  }));
}

export function useTasks(workId?: string) {
  const [tasks, setTasks] = useState<Task2[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Convert database task to Task2 format
  const convertDBTaskToTask2 = useCallback((dbTask: TaskFromDB): Task2 => {
    // O primeiro work_tag é a obra vinculada; resolvemos o nome real da obra
    // (antes exibia "Obra <uuid>"). projectId/workId apontam para a mesma obra.
    const obraTag = dbTask.work_tags.length > 0 ? dbTask.work_tags[0] : undefined;
    const obra = obraTag ? getObraById(obraTag) : undefined;
    // Responsável: resolve o uuid contra os membros hidratados no login. Sem
    // isto a delegação era write-only — gravava assignee_id e nunca exibia.
    const membro = getMembroById(dbTask.assignee_id);
    return {
      id: dbTask.id,
      title: dbTask.title,
      description: dbTask.description || '',
      status: dbTask.status,
      priority: dbTask.priority as 'low' | 'medium' | 'high',
      projectId: obraTag ?? 'sem-obra',
      projectName: obra?.nomeObra ?? 'Sem obra',
      workId: obraTag,
      assignees: membro
        ? [{ id: membro.id, name: membro.nome, avatar: membro.avatarUrl ?? undefined }]
        : [],
      followers: parseFollowers(dbTask.followers),
      // work_tags[0] é a obra (exibida como projectName); o setor são as demais tags.
      setor: dbTask.work_tags.slice(1).join(', '),
      tipo: 'tarefa',
      startDate: dbTask.start_date ? new Date(dbTask.start_date) : undefined,
      endDate: dbTask.due_date ? new Date(dbTask.due_date) : undefined,
      createdAt: new Date(dbTask.created_at),
      updatedAt: new Date(dbTask.updated_at),
      comments: parseComments(dbTask.comments),
      checklist: parseChecklist(dbTask.checklist),
      attachments: []
    };
  }, []);

  // Fetch tasks from Supabase
  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('tasks')
        .select('*')
        .order('created_at', { ascending: false });

      // Filter by work ID if provided
      if (workId) {
        query = query.contains('work_tags', [workId]);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      const convertedTasks = (data as TaskFromDB[]).map(convertDBTaskToTask2);
      setTasks(convertedTasks);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar tarefas';
      setError(errorMessage);
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [workId, convertDBTaskToTask2]);

  // Create a new task
  const createTask = useCallback(async (taskData: CreateTaskData): Promise<Task2 | null> => {
    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('Usuário não autenticado');
      }

      // Prepare work tags array
      const workTags = [];
      if (taskData.workId) {
        workTags.push(taskData.workId);
      }
      if (taskData.setor) {
        workTags.push(taskData.setor);
      }

      const taskInsert = {
        user_id: user.id,
        title: taskData.title,
        description: taskData.description || null,
        status: taskData.status || 'not_started',
        priority: taskData.priority,
        due_date: taskData.endDate ? taskData.endDate.toISOString() : null,
        // A data de início escolhida no formulário era descartada por não haver
        // coluna; agora persiste (migration 20260726190000).
        start_date: taskData.startDate ? taskData.startDate.toISOString() : null,
        work_tags: workTags,
        assignee_id: taskData.assigneeId || null,
        project_id: taskData.projectId || null,
        progress: 0
      };

      let { data, error } = await supabase
        .from('tasks')
        .insert(taskInsert)
        .select()
        .single();

      // Mesmo fallback da edição: sem a migration, cria a tarefa sem os campos
      // novos em vez de falhar por completo.
      if (error && isMissingColumnError(error)) {
        colunasNovasDisponiveis = false;
        const semColunasNovas = { ...taskInsert } as Record<string, unknown>;
        COLUNAS_NOVAS.forEach((c) => delete semColunasNovas[c]);
        ({ data, error } = await supabase
          .from('tasks')
          .insert(semColunasNovas as typeof taskInsert)
          .select()
          .single());
      }

      if (error) {
        throw error;
      }

      const newTask = convertDBTaskToTask2(data as TaskFromDB);
      
      // Add to local state
      setTasks(prev => [newTask, ...prev]);

      toast({
        title: 'Tarefa criada com sucesso!',
        description: `A tarefa "${taskData.title}" foi adicionada ao sistema.`,
      });

      return newTask;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao criar tarefa';
      setError(errorMessage);
      toast({
        title: 'Erro ao criar tarefa',
        description: errorMessage,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [convertDBTaskToTask2]);

  // Update task status
  const updateTaskStatus = useCallback(async (taskId: string, status: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', taskId);

      if (error) {
        throw error;
      }

      // Update local state
      setTasks(prev => prev.map(task => 
        task.id === taskId 
          ? { ...task, status, updatedAt: new Date() }
          : task
      ));

      toast({
        title: 'Status atualizado',
        description: 'Status da tarefa atualizado com sucesso.',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar status';
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  }, []);

  // Update task fields. Persiste tudo o que o modal de detalhes permite editar —
  // antes só título/descrição/prioridade/status/prazo iam para o banco e o
  // restante (responsável, comentários, checklist, seguidores, setor, início)
  // era descartado ao fechar, apesar do rótulo "Tudo sincronizado".
  const updateTask = useCallback(async (taskId: string, patch: Partial<Task2>): Promise<void> => {
    try {
      const dbPatch: TablesUpdate<'tasks'> = { updated_at: new Date().toISOString() };
      if (patch.title !== undefined) dbPatch.title = patch.title;
      if (patch.description !== undefined) dbPatch.description = patch.description || null;
      if (patch.priority !== undefined) dbPatch.priority = patch.priority;
      if (patch.status !== undefined) dbPatch.status = patch.status;
      if (patch.endDate !== undefined) {
        dbPatch.due_date = patch.endDate ? patch.endDate.toISOString() : null;
      }
      if (patch.startDate !== undefined) {
        dbPatch.start_date = patch.startDate ? patch.startDate.toISOString() : null;
      }
      if (patch.assignees !== undefined) {
        dbPatch.assignee_id = patch.assignees[0]?.id ?? null;
      }
      // Datas viram string ISO no jsonb (JSON.parse/stringify pelo cliente).
      if (patch.comments !== undefined) dbPatch.comments = patch.comments as unknown as Json;
      if (patch.checklist !== undefined) dbPatch.checklist = patch.checklist as unknown as Json;
      if (patch.followers !== undefined) dbPatch.followers = patch.followers as unknown as Json;
      // O setor mora em work_tags[1..]; work_tags[0] continua sendo a obra.
      if (patch.setor !== undefined) {
        const atual = tasks.find(t => t.id === taskId);
        const obraTag = atual?.workId;
        const setores = patch.setor.split(',').map(s => s.trim()).filter(Boolean);
        dbPatch.work_tags = obraTag ? [obraTag, ...setores] : setores;
      }

      let { error } = await supabase.from('tasks').update(dbPatch).eq('id', taskId);

      // Banco sem a migration das colunas novas: em vez de perder a edição
      // inteira (inclusive título e status, que existem há muito), grava o que
      // dá e avisa. A capacidade fica memorizada para não repetir a ida à rede.
      if (error && isMissingColumnError(error)) {
        colunasNovasDisponiveis = false;
        const semColunasNovas = { ...dbPatch };
        COLUNAS_NOVAS.forEach((c) => delete semColunasNovas[c]);
        ({ error } = await supabase.from('tasks').update(semColunasNovas).eq('id', taskId));
        if (!error) {
          toast({
            title: 'Salvo parcialmente',
            description:
              'Comentários, checklist, seguidores e data de início não foram gravados: o banco ainda não tem a migration destas colunas.',
            variant: 'destructive',
          });
        }
      }
      if (error) throw error;

      setTasks(prev => prev.map(task =>
        task.id === taskId ? { ...task, ...patch, updatedAt: new Date() } : task
      ));

      if (colunasNovasDisponiveis) {
        toast({
          title: 'Tarefa atualizada',
          description: 'As alterações foram salvas.',
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar tarefa';
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  }, [tasks]);

  // Load tasks on mount and when workId changes
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  return {
    tasks,
    loading,
    error,
    createTask,
    updateTaskStatus,
    updateTask,
    refetch: fetchTasks
  };
}